import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { useReducer, type ReactElement } from 'react';

import { BrandPanel } from './_components/BrandPanel';
import { LoginStep } from './_steps/LoginStep';
import { TotpStep } from './_steps/TotpStep';
import { initialLoginState, loginReducer } from './loginStateMachine';
import { tokens } from '../../design/tokens';

/**
 * LoginPage — admin 2-step login wizard (AUT-110 rework).
 *
 * The flow lives on a single URL (`/auth/login`); the state machine in
 * `loginStateMachine.ts` drives which step renders. F5 mid-flow resets
 * the state (acceptable for v1, ≤20 admin users, internal).
 *
 *   step=login → <LoginStep /> — submits to `/admin/auth/login`
 *   step=totp  → <TotpStep />  — submits to `/auth/totp/challenge`
 *
 * Layout is a two-column flex: left brand panel (visual continuity,
 * step progress timeline), right form card. BrandPanel renders the
 * step-progress dots so the operator always sees where they are.
 *
 * Terminal success: `authStore` writes the session + each step navigates
 * to Dashboard from its own `.then()` callback (avoids unmounted-component
 * warnings from a `useEffect` watcher).
 */
export const LoginPage = (): ReactElement => {
  const [state, dispatch] = useReducer(loginReducer, initialLoginState);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: tokens.surface.base }}>
      <BrandPanel step={state.step} email={state.email} />
      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '56px',
        }}
      >
        <Paper elevation={0} sx={{ width: '100%', maxWidth: 420, background: 'transparent' }}>
          {state.step === 'login' && <LoginStep state={state} dispatch={dispatch} />}
          {state.step === 'totp' && <TotpStep state={state} dispatch={dispatch} />}
        </Paper>
      </Box>
    </Box>
  );
};
